import React, { useState, useEffect } from 'react';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { fmt } from '@/lib/formatters';

/**
 * GoalCarousel — horizontal carousel showing the main "monto liquidable"
 * card for each active goal, sorted DESC by totalAmount.
 */
export default function GoalCarousel({ activeGoals }) {
  const [carouselApi, setCarouselApi] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (!carouselApi) return;
    const onSelect = () => setCurrentSlide(carouselApi.selectedScrollSnap());
    carouselApi.on('select', onSelect);
    onSelect();
    return () => { carouselApi.off('select', onSelect); };
  }, [carouselApi]);

  if (activeGoals.length === 0) return null;

  const showSwipeHint = activeGoals.length > 1;

  return (
    <div className="px-6 md:px-8 pb-4" data-testid="goals-carousel">
      <Carousel opts={{ align: 'start', loop: activeGoals.length > 1 }} setApi={setCarouselApi}>
        <CarouselContent className="-ml-0">
          {activeGoals.map((goal, idx) => {
            // Color rule (per Bloque 4 spec):
            //   • BLUE  → KOMPA+ Ahorro Total puro (modality='total' AND not 'grupal')
            //   • GOLD  → Plan Acelerado (modality='club') OR KOMPA+ Ahorro entre Kompas (subModality='grupal')
            const isAhorroTotalPuro =
              goal.modality === 'total' &&
              goal.subModality !== 'grupal' &&
              goal.planCode !== 'acelerado';
            const bg = isAhorroTotalPuro
              ? 'bg-[#1f2d5a]'
              : 'bg-gradient-to-br from-[#fbb040] to-[#e09a20]';
            const textMain = isAhorroTotalPuro ? 'text-white' : 'text-black';
            const textSub = isAhorroTotalPuro ? 'text-white/50' : 'text-black/50';
            const textMuted = isAhorroTotalPuro ? 'text-white/40' : 'text-black/40';
            return (
              <CarouselItem key={goal.id} className="pl-0">
                <div className={`${bg} rounded-2xl p-5 text-center`}>
                  <p className={`text-xs uppercase tracking-widest ${textSub} font-medium mb-1`}>
                    Monto liquidable &middot; {goal.label}
                  </p>
                  <p className={`text-3xl md:text-4xl font-black ${textMain} tabular-nums tracking-tight`} data-testid={`carousel-amount-${goal.id}`}>
                    &euro; {fmt(goal.savedAmount)}
                  </p>
                  {showSwipeHint && (
                    <p className={`text-xs ${textMuted} mt-1`} data-testid={`carousel-hint-${goal.id}`}>
                      {idx === 0
                        ? `${activeGoals.length} metas activas - Deslizar para ver m\u00e1s`
                        : 'Deslizar para ver m\u00e1s'
                      }
                    </p>
                  )}
                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>
      </Carousel>
      {activeGoals.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-3" data-testid="carousel-dots">
          {activeGoals.map((goal, i) => (
            <button
              key={goal.id}
              onClick={() => carouselApi?.scrollTo(i)}
              className={`h-1.5 rounded-full transition-all ${i === currentSlide ? 'bg-[#fbb040] w-4' : 'bg-gray-300 w-1.5'}`}
              data-testid={`carousel-dot-${i}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
